import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';

import { TSDCreateData, TSDCreateDataSchema } from '@e-origin/contracts';
import { TSDMasterConsignmentInputMode, TSDType } from '@e-origin/shared';

import { useCreateTSD } from '@hooks/tsd';

interface UseNewTSDFormInitializerProps {
  tsdView: TSDType;
  onHide: () => void;
  afterCreate: () => void;
}
export const DEFAULT_DECLARATION_DATE = new Date(Date.now() - 5 * 60 * 1000);

export const DEFAULT_DATE_AND_TIME_OF_PRESENTATION_OF_THE_GOODS = new Date(Date.now() - 2 * 60 * 1000);

export const useNewTSDFormInitializer = ({ tsdView, onHide, afterCreate }: UseNewTSDFormInitializerProps) => {
  const [submitting, setSubmitting] = useState(false);
  const createTSDMutation = useCreateTSD();

  const buildFormValues = (type: TSDType): TSDCreateData => ({
    name: '',
    customer: '',
    template: '',
    type,
    file: null,
    masterConsignmentInputMode: type === TSDType.TRANSFERRED ? TSDMasterConsignmentInputMode.MANUAL : TSDMasterConsignmentInputMode.UPLOAD,
    generalInfo: {
      declarationDate: DEFAULT_DECLARATION_DATE,
      ensReUseIndicator: false,
      dateAndTimeOfPresentationOfTheGoods: DEFAULT_DATE_AND_TIME_OF_PRESENTATION_OF_THE_GOODS,
    },
    consignment: {
      master: {
        totalNumberOfPackages: '',
        totalGrossMass: '',
        transportDocuments: [
          {
            type: '',
            referenceNumber: '',
          },
        ],
      },
    },
    locationOfGoodsUNLocode: '',
    warehouse: '',
    messageInfo: {
      LRN: '',
      MRN: '',
    },
  });

  const form = useForm<TSDCreateData>({
    initialValues: buildFormValues(tsdView),
    validate: zodResolver(TSDCreateDataSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values: TSDCreateData) => {
    try {
      setSubmitting(true);
      const payload = {
        name: values.name,
        customer: values.customer,
        type: values.type,
        ...(values.template && { template: values.template }),
        locationOfGoodsUNLocode: values.locationOfGoodsUNLocode,
        warehouse: values.warehouse,
        ...([TSDType.COMBINED, TSDType.PRELODGED].includes(values.type) && {
          file: values.file,
          generalInfo: {
            declarationDate: values.generalInfo.declarationDate,
            ensReUseIndicator: values.generalInfo.ensReUseIndicator,
            ...(values.type === TSDType.COMBINED && {
              dateAndTimeOfPresentationOfTheGoods: values.generalInfo.dateAndTimeOfPresentationOfTheGoods,
            }),
          },
        }),

        ...(values.masterConsignmentInputMode === TSDMasterConsignmentInputMode.MANUAL && {
          consignment: {
            master: {
              totalNumberOfPackages: values.consignment.master.totalNumberOfPackages,
              totalGrossMass: values.consignment.master.totalGrossMass,
              ...(values.type === TSDType.TRANSFERRED && {
                transportDocuments: values.consignment?.master?.transportDocuments,
              }),
            },
          },
        }),
        ...(values.type === TSDType.TRANSFERRED && {
          messageInfo: {
            LRN: values.messageInfo.LRN,
            MRN: values.messageInfo.MRN,
          },
        }),
      };
      await createTSDMutation.mutateAsync(payload);
      afterCreate();
      onHide();
    } catch (err) {
      // silent fail - toast handled
    } finally {
      setSubmitting(false);
    }
  };

  return { form, onSubmit, submitting, buildFormValues };
};
